import React, { useState, useEffect, useCallback } from 'react';
import { formatValueSimple } from "../../../utils/cellValueFormatter";
import {
  Badge,
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from "@mantis-sas/ui";
import { ArrowLeft, FileText, Calendar, MessageSquare, Info, Building, StickyNote, Edit, Clock, AlertCircle, CheckCircle2, XCircle, UserCheck, Package, Settings, TrendingUp, Star, Upload, Paperclip, X } from "lucide-react";
import OverviewSection from "./Section/OverviewSection";
import WorkflowSection from "./Section/WorkflowSection";
import DocumentsSection from "./Section/DocumentsSection";
import LegalSection from "./Section/LegalSection";
import { CommentsSection } from "./components/CommentsSection";
import AIInsightsSection from "./components/AIInsightsSection";
import EditIPRModal from "./components/EditIPRModal";
import { connect } from "react-redux";
import { selectAuthState } from "../../../store/selectors/authSelectors";
import {
  getOrderItems,
  getPurchaseOrdersByIPRId,
} from "../../../ApiCalls/mantisFlowsManangment";
import "../../../ApiCalls/mantisIA";
import { createFileAndAttach } from "../../../ApiCalls/mantisFlowsFiles";
import "./ControlPanelDetail.css";

// Agregar estilos adicionales
const additionalStyles = `
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(249, 115, 22, 0.3) transparent;
  }
  
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(249, 115, 22, 0.3);
    border-radius: 3px;
    transition: background-color 0.2s ease;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(249, 115, 22, 0.5);
  }
  
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-5px); }
  }
  
  .floating-animation {
    animation: float 3s ease-in-out infinite;
  }
  
  /* Mobile optimization */
  @media (max-width: 768px) {
    .control-panel-container {
      min-height: 100vh;
      min-height: 100dvh;
    }
    
    .glass-morphism {
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
    }
    
    .hover-lift:active {
      transform: translateY(1px);
    }
    
    .interactive-element {
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
    }
    
    /* Better text legibility on mobile — this screen's only (B-174: the sheet is injected for the whole
       app on import, and unscoped these rules resized every button, checkbox, switch and text of Cierres). */
    .control-panel-container .text-xs {
      font-size: 0.75rem;
      line-height: 1.2;
    }
    
    .control-panel-container .text-sm {
      font-size: 0.875rem;
      line-height: 1.25;
    }
    
    /* Improved touch targets */
    .control-panel-container button, .control-panel-container .cursor-pointer {
      min-height: 44px;
      min-width: 44px;
      touch-action: manipulation;
    }
    
    /* Prevent zoom on input focus */
    .control-panel-container input, .control-panel-container select, .control-panel-container textarea {
      font-size: 16px;
    }
  }
  
  /* Enhanced animations for mobile */
  @media (max-width: 768px) {
    .animate-fade-in-up {
      animation: fadeInUpMobile 0.5s ease-out;
    }
    
    .animate-slide-in-left {
      animation: slideInLeftMobile 0.4s ease-out;
    }
    
    .animate-slide-in-right {
      animation: slideInRightMobile 0.4s ease-out;
    }
  }
  
  @keyframes fadeInUpMobile {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes slideInLeftMobile {
    from {
      opacity: 0;
      transform: translateX(-15px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @keyframes slideInRightMobile {
    from {
      opacity: 0;
      transform: translateX(15px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

// Inyectar estilos
if (typeof document !== "undefined") {
  const existingStyleCustom = document.getElementById(
    "control-panel-custom-styles"
  );
  if (!existingStyleCustom) {
    const styleSheet = document.createElement("style");
    styleSheet.id = "control-panel-custom-styles";
    styleSheet.textContent = additionalStyles;
    document.head.appendChild(styleSheet);
  }
}

/**
 * ControlPanelDetail Component
 *
 * This component now includes:
 * - Direct purchase order fetching via getPurchaseOrdersByIPRId API
 * - Real-time purchase order data display when IPR is approved
 * - Fallback to order items when purchase order items are not available
 * - Loading states and refresh functionality
 * - Debug information in development mode
 */
function ControlPanelDetail({
  data: initialData,
  onBack,
  currentUser,
  clientId,
  mantisBusiness,
  token,
}) {
  const [activeTab, setActiveTab] = useState("overview");
  const [sidebarTab, setSidebarTab] = useState("summary");
  const [data, setData] = useState(initialData);

  // Log para verificar que los datos del usuario llegan correctamente
  console.log("🔍 ControlPanelDetail - Datos del usuario recibidos:", {
    currentUser: currentUser,
    currentUserId: currentUser?._id,
    currentUserEmail:
      currentUser?.email ||
      currentUser?.corporateEmail ||
      currentUser?.personalEmail,
    mantisBusiness: mantisBusiness,
    clientId: clientId,
  });
  const [orderItems, setOrderItems] = useState([]);
  const [updatedOrderItems, setUpdatedOrderItems] = useState(null); // For local updates
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [, setIsLoadingPurchaseOrders] = useState(false);
  const [aiInsights, setAiInsights] = useState(null);
  const [isLoadingInsights] = useState(false);
  const [insightsError, setInsightsError] = useState(null);
  const [, setIsMobile] = useState(false);

  // File attachment state
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [showFileUpload, setShowFileUpload] = useState(false);

  // Edit IPR modal state
  const [showEditModal, setShowEditModal] = useState(false);

  const { toast } = useToast();

  // Update local data when initialData prop changes
  useEffect(() => {
    setData(initialData);
  }, [initialData]);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  console.log("🔍 ControlPanelDetail - Datos completos recibidos:", data);
  console.log("🔍 ControlPanelDetail - AI Insights específicos:", {
    ia_insights: data?.ia_insights,
    ai_insights: data?.ai_insights,
    type_ia: typeof data?.ia_insights,
    type_ai: typeof data?.ai_insights,
    keys_ia:
      data?.ia_insights && typeof data?.ia_insights === "object"
        ? Object.keys(data.ia_insights)
        : null,
    keys_ai:
      data?.ai_insights && typeof data?.ai_insights === "object"
        ? Object.keys(data.ai_insights)
        : null,
  });
  // Manejar acción de editar IPR
  const handleEditIPR = () => {
    // Abrir nueva pestaña
    window.open(
      `/form/${data.form_share_id}/${data.form_submission_id}`,
      "_blank"
    );
  };

  // Manejar acción de editar IPR (modal)
  const handleEditIPRModal = () => {
    setShowEditModal(true);
  };

  // Manejar actualización del IPR después de editar
  const handleIPRUpdate = (updatedData) => {
    // Update the local data state with the new values
    if (updatedData) {
      setData(updatedData);
      console.log("IPR actualizado con datos:", updatedData);
    }
  };

  // File handling functions
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setShowFileUpload(true);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile || !clientId || !data?.id) {
      toast({
        title: "Error",
        description:
          "Por favor selecciona un archivo y asegúrate de que el IPR esté cargado",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsUploadingFile(true);

      // Convert file to base64
      const base64 = await convertFileToBase64(selectedFile);

      // Prepare file data
      const fileData = {
        base64: base64,
        filename: selectedFile.name,
        mime_type: selectedFile.type,
        size_in_bytes: selectedFile.size,
        uploaded_by: currentUser?._id || currentUser?.email || "unknown",
      };

      // Call the API to create and attach file
      const result = await createFileAndAttach(
        clientId,
        fileData,
        "initial_purchase_requests", // entity table
        data.id, // entity ID
        token
      );

      if (result.success) {
        toast({
          title: "Éxito",
          description: "Archivo adjuntado correctamente al IPR",
          variant: "default",
        });

        // Reset form
        setSelectedFile(null);
        setShowFileUpload(false);

        // Optionally refresh the documents section or trigger a callback
        // You might want to add a callback prop to refresh documents
      } else {
        throw new Error(result.message || "Error al adjuntar el archivo");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toast({
        title: "Error",
        description: `Error al adjuntar el archivo: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsUploadingFile(false);
    }
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = (error) => reject(error);
    });
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    setShowFileUpload(false);
  };

  // Cargar items de la orden
  const loadOrderItems = useCallback(async () => {
    if (!clientId || !data?.id) return;

    try {
      setIsLoadingItems(true);
      const items = await getOrderItems(clientId, data.id);
      setOrderItems(items.data || []);
    } catch (error) {
      console.error("Error loading order items:", error);
      toast({
        title: "Error",
        description: "Error al cargar los artículos de la orden",
        variant: "destructive",
      });
    } finally {
      setIsLoadingItems(false);
    }
  }, [clientId, data?.id, toast]);

  // Cargar órdenes de compra
  const loadPurchaseOrders = useCallback(async () => {
    if (!clientId || !data?.id) return;

    try {
      setIsLoadingPurchaseOrders(true);
      const orders = await getPurchaseOrdersByIPRId(clientId, {}, data.id);
      setPurchaseOrders(orders.data || []);
    } catch (error) {
      console.error("Error loading purchase orders:", error);
      toast({
        title: "Error",
        description: "Error al cargar las órdenes de compra",
        variant: "destructive",
      });
    } finally {
      setIsLoadingPurchaseOrders(false);
    }
  }, [clientId, data?.id, toast]);

  // Cargar items cuando se monta el componente o cambian los datos
  useEffect(() => {
    if (data?.id && clientId) {
      loadOrderItems();
      loadPurchaseOrders();
    }
  }, [data?.id, clientId, loadOrderItems, loadPurchaseOrders]);

  // Cargar insights de IA desde los datos del IPR
  useEffect(() => {
    console.log("🔍 ControlPanelDetail - Verificando ia_insights en data:", {
      data: data,
      ia_insights: data?.ia_insights,
      ia_insights_type: typeof data?.ia_insights,
      ia_insights_keys: data?.ia_insights
        ? Object.keys(data.ia_insights)
        : null,
    });

    // Limpiar errores previos
    setInsightsError(null);

    if (data?.ia_insights) {
      // Si ia_insights es un string, intentar parsearlo
      if (typeof data.ia_insights === "string") {
        try {
          const parsedInsights = JSON.parse(data.ia_insights);
          console.log("✅ Insights parseados correctamente:", parsedInsights);
          setAiInsights(parsedInsights);
        } catch (error) {
          console.error("❌ Error al parsear ia_insights:", error);
          setInsightsError("Error al procesar los insights de IA");
          setAiInsights(null);
        }
      } else if (typeof data.ia_insights === "object") {
        // Si ya es un objeto, usarlo directamente
        console.log("✅ Insights ya son objeto:", data.ia_insights);
        setAiInsights(data.ia_insights);
      }
    } else {
      console.log("ℹ️ No hay ia_insights en el IPR");
      setAiInsights(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.ia_insights]);

  // Use updated items if available, otherwise use original orderItems
  const displayOrderItems = updatedOrderItems || orderItems;
  const summaryData = {
    totalPrice: data?.total_amount || "$0.00",
    paymentMethod: "Orden de compra",
    workflow: "Solicitud de Compra",
    category: data?.category || "No especificado",
    subcategory: data?.subcategory || "No especificado",
    department: data?.area || data?.department || "No especificado",
    requester: data?.created_by_name || "Desconocido",
    title: data?.title || "Solicitud sin título",
    status: data?.status || "pendiente",
    criticality: data?.criticality || "baja",
    deliveryDate: data?.delivery_target_date || "No establecido",
    createdAt: data?.created_at?.slice(0, 10) || "No disponible",
    custom_fields: data?.custom_fields || "No especificado",
    amount: data?.amount || 0,
    currency: data?.currency || "USD",
    justification: data?.justification || "No especificada",
  };

  // Check if the initial purchase request is approved
  const isIPRApproved =
    data?.status === "approved" || data?.status === "aprobado";

  // Get purchase order data if available (regardless of status)
  const purchaseOrder = purchaseOrders?.[0]; // Get first PO if exists
  const purchaseOrderItems = orderItems || []; // Use orderItems as fallback for PO items

  const getCriticalityColor = (criticality) => {
    switch (criticality?.toLowerCase()) {
      case "high":
      case "alta":
        return "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700";
      case "medium":
      case "media":
        return "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700";
      default:
        return "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700";
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
      case "aprobado":
        return "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700";
      case "pending":
      case "pendiente":
        return "bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700";
      case "rejected":
      case "rechazado":
        return "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-900/30 dark:text-gray-300 dark:border-gray-700";
    }
  };

  const getStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return "aprobado";
      case "pending":
        return "pendiente";
      case "rejected":
        return "rechazado";
      default:
        return status;
    }
  };

  const getCriticalityText = (criticality) => {
    switch (criticality?.toLowerCase()) {
      case "high":
        return "alta";
      case "medium":
        return "media";
      case "low":
        return "baja";
      default:
        return criticality;
    }
  };

  return (
    <div className="flex flex-col md:flex-row h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 control-panel-container animate-fade-in-up touch-manipulation">
      {/* Main Content Area - Full width on mobile, 70% on desktop */}
      <div className="flex flex-1 flex-col overflow-y-auto animate-slide-in-left">
        {/* Top Header with Back Button and Edit IPR Button */}
        <div className="sticky top-0 bg-white/95 dark:bg-slate-800/95 backdrop-blur-lg z-20 border-b border-orange-200/50 dark:border-slate-700/50 shadow-sm glass-morphism">
          <div className="flex items-center justify-between p-3 md:p-6">
            <div className="flex items-center space-x-3 md:space-x-6">
              <Button
                variant="ghost"
                size="sm"
                onClick={onBack}
                className="flex items-center space-x-1 md:space-x-2 text-slate-600 hover:text-orange-600 dark:text-slate-400 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all duration-200 rounded-xl px-2 md:px-4 py-2 interactive-element hover-lift text-sm md:text-base"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="font-medium hidden sm:inline">Volver</span>
              </Button>

              <div className="h-6 md:h-8 w-px bg-gradient-to-b from-transparent via-slate-300 to-transparent dark:via-slate-600"></div>

              <div className="flex items-center space-x-2 md:space-x-4">
                <div className="relative">
                  <div
                    className={`w-8 h-8 md:w-12 md:h-12 rounded md:rounded-xl flex items-center justify-center shadow-lg ${
                      isIPRApproved
                        ? "bg-gradient-to-r from-green-500 to-emerald-500"
                        : "bg-gradient-to-r from-orange-500 to-amber-500"
                    }`}
                  >
                    {isIPRApproved ? (
                      <CheckCircle2 className="h-4 w-4 md:h-6 md:w-6 text-white" />
                    ) : (
                      <FileText className="h-4 w-4 md:h-6 md:w-6 text-white" />
                    )}
                  </div>
                  <div className="absolute -top-1 -right-1 w-3 h-3 md:w-4 md:h-4 bg-green-500 rounded-full border-1 md:border-2 border-white dark:border-slate-800 flex items-center justify-center">
                    <div className="w-1 h-1 md:w-2 md:h-2 bg-white rounded-full"></div>
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center space-x-1 md:space-x-2">
                    <span className="text-sm md:text-lg font-bold text-slate-900 dark:text-slate-100">
                      {isIPRApproved
                        ? `Orden de Compra`
                        : `${summaryData.title} - ${summaryData.requester}`}
                    </span>
                    <Badge
                      className={`text-xs font-medium px-1 md:px-2 py-1 rounded-full ${getCriticalityColor(
                        summaryData.criticality
                      )} hidden sm:flex items-center`}
                    >
                      <Star className="h-2 w-2 md:h-3 md:w-3 mr-1" />
                      {getCriticalityText(summaryData.criticality)}
                    </Badge>
                  </div>
                  <div className="text-xs md:text-sm text-slate-600 dark:text-slate-400 max-w-32 md:max-w-md truncate">
                    {isIPRApproved
                      ? `PO Aprobada - IPR #${data?.id || "N/A"}`
                      : `IPR #${data?.id || "N/A"}`}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-1 md:space-x-3">
              <Badge
                className={`text-xs md:text-sm font-medium px-2 md:px-4 py-1 md:py-2 rounded-full ${
                  isIPRApproved
                    ? "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700"
                    : getStatusColor(summaryData.status)
                }`}
              >
                {isIPRApproved ? (
                  <>
                    <CheckCircle2 className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                    <span className="hidden sm:inline">
                      Orden de Compra Aprobada
                    </span>
                  </>
                ) : summaryData.status === "approved" ||
                  summaryData.status === "aprobado" ? (
                  <>
                    <CheckCircle2 className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                    <span className="hidden sm:inline">
                      {getStatusText(summaryData.status)}
                    </span>
                  </>
                ) : summaryData.status === "rejected" ||
                  summaryData.status === "rechazado" ? (
                  <>
                    <XCircle className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                    <span className="hidden sm:inline">
                      {getStatusText(summaryData.status)}
                    </span>
                  </>
                ) : (
                  <>
                    <Clock className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                    <span className="hidden sm:inline">
                      {getStatusText(summaryData.status)}
                    </span>
                  </>
                )}
              </Badge>

              {data.form_submission_id && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEditIPR}
                  className="flex items-center space-x-1 md:space-x-2 text-orange-600 border-orange-300 hover:bg-orange-50 dark:text-orange-400 dark:border-orange-600 dark:hover:bg-orange-900/30 transition-all duration-200 rounded md:rounded-xl px-2 md:px-4 py-1 md:py-2 font-medium interactive-element hover-lift border-glow text-xs md:text-sm"
                >
                  <Edit className="h-3 w-3 md:h-4 md:w-4" />
                  <span className="hidden sm:inline">Editar Formulario</span>
                </Button>
              )}

              {/* Edit IPR Fields Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleEditIPRModal}
                className="flex items-center space-x-1 md:space-x-2 text-blue-600 border-blue-300 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-600 dark:hover:bg-blue-900/30 transition-all duration-200 rounded md:rounded-xl px-2 md:px-4 py-1 md:py-2 font-medium interactive-element hover-lift border-glow text-xs md:text-sm"
              >
                <Edit className="h-3 w-3 md:h-4 md:w-4" />
                <span className="hidden sm:inline">Editar IPR</span>
              </Button>

              {/* File Upload Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => document.getElementById("file-input").click()}
                className="flex items-center space-x-1 md:space-x-2 text-blue-600 border-blue-300 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-600 dark:hover:bg-blue-900/30 transition-all duration-200 rounded md:rounded-xl px-2 md:px-4 py-1 md:py-2 font-medium interactive-element hover-lift border-glow text-xs md:text-sm upload-button"
              >
                <Upload className="h-3 w-3 md:h-4 md:w-4" />
                <span className="hidden sm:inline">Adjuntar Archivo</span>
              </Button>

              {/* Hidden file input */}
              <input
                id="file-input"
                type="file"
                onChange={handleFileSelect}
                accept="*/*"
                className="hidden"
              />
            </div>
          </div>
        </div>

        {/* Conditional Content: Regular Tabs View */}
        <div className="mx-3 md:mx-6 mt-3 md:mt-6">
          {/* Show Regular Tabs View */}
          <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-xl md:rounded-2xl border border-orange-200/30 dark:border-slate-700/30 shadow-xl shadow-orange-500/5 dark:shadow-slate-900/20 overflow-hidden">
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
            >
              <TabsList className="w-full grid grid-cols-3 bg-gradient-to-r from-orange-50/50 via-amber-50/30 to-orange-50/50 dark:bg-gradient-to-r dark:from-slate-800/50 dark:via-slate-700/30 dark:to-slate-800/50 p-1 md:p-2 border-b border-orange-100/50 dark:border-slate-700/50">
                <TabsTrigger
                  value="overview"
                  className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 data-[state=active]:text-orange-600 dark:data-[state=active]:text-orange-400 data-[state=active]:shadow-lg data-[state=active]:shadow-orange-500/10 text-slate-600 dark:text-slate-400 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-white/50 dark:hover:bg-slate-700/50 rounded md:rounded-xl transition-all duration-300 font-medium py-2 md:py-3 text-xs md:text-sm"
                >
                  <div className="flex flex-col md:flex-row items-center space-y-1 md:space-y-0 md:space-x-3">
                    <div className="relative">
                      <TrendingUp className="h-4 w-4 md:h-5 md:w-5" />
                      <div className="absolute -top-1 -right-1 w-1.5 h-1.5 md:w-2 md:h-2 bg-orange-500 rounded-full opacity-75"></div>
                    </div>
                    <span className="hidden sm:inline">Resumen</span>
                  </div>
                </TabsTrigger>

                <TabsTrigger
                  value="documents"
                  className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 data-[state=active]:text-orange-600 dark:data-[state=active]:text-orange-400 data-[state=active]:shadow-lg data-[state=active]:shadow-orange-500/10 text-slate-600 dark:text-slate-400 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-white/50 dark:hover:bg-slate-700/50 rounded md:rounded-xl transition-all duration-300 font-medium py-2 md:py-3 text-xs md:text-sm"
                >
                  <div className="flex flex-col md:flex-row items-center space-y-1 md:space-y-0 md:space-x-3">
                    <FileText className="h-4 w-4 md:h-5 md:w-5" />
                    <span className="hidden sm:inline">Documentos</span>
                  </div>
                </TabsTrigger>
                {/* <TabsTrigger
                    value="legal"
                    className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 data-[state=active]:text-orange-600 dark:data-[state=active]:text-orange-400 data-[state=active]:shadow-lg data-[state=active]:shadow-orange-500/10 text-slate-600 dark:text-slate-400 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-white/50 dark:hover:bg-slate-700/50 rounded md:rounded-xl transition-all duration-300 font-medium py-2 md:py-3 text-xs md:text-sm"
                  >
                    <div className="flex flex-col md:flex-row items-center space-y-1 md:space-y-0 md:space-x-3">
                      <CheckSquare className="h-4 w-4 md:h-5 md:w-5" />
                      <span className="hidden sm:inline">Aspectos Legales</span>
                    </div>
                  </TabsTrigger> */}
              </TabsList>

              {/* Content Sections */}
              <div className="p-4 md:p-8">
                <TabsContent
                  value="overview"
                  className="mt-0 space-y-4 md:space-y-6"
                >
                  <div className="flex items-center gap-2 mb-2"></div>
                  {/* Summary Card - Responsive horizontal layout */}
                  <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl rounded-xl md:rounded-2xl p-3 md:p-5 border border-orange-200/30 dark:border-slate-700/30 shadow-xl shadow-orange-500/5 glass-morphism animate-fade-in-up mb-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      {/* Solicitante */}
                      <div className="flex flex-col items-start md:border-r md:border-orange-100/50 dark:md:border-slate-700/50 pr-0 md:pr-4">
                        <div className="flex items-center gap-2 mb-2">
                          <UserCheck className="h-4 w-4 text-purple-500" />
                          <span className="font-bold text-slate-900 dark:text-slate-100 text-sm">
                            Solicitante
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center">
                            <span className="text-white font-bold text-base">
                              {summaryData.requester.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <div className="font-bold text-slate-900 dark:text-slate-100 text-xs md:text-sm">
                              {summaryData.requester}
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Detalles de la Solicitud */}
                      <div className="flex flex-col items-start md:border-r md:border-orange-100/50 dark:md:border-slate-700/50 pr-0 md:pr-4">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText className="h-4 w-4 text-orange-500" />
                          <span className="font-bold text-slate-900 dark:text-slate-100 text-sm">
                            Detalles
                          </span>
                        </div>
                        <div className="text-xs text-slate-600 dark:text-slate-400 mb-1">
                          Depto:{" "}
                          <span className="font-semibold">
                            {summaryData.department}
                          </span>
                        </div>
                        {summaryData.amount > 0 && (
                          <div className="text-xs text-slate-600 dark:text-slate-400 mb-1">
                            Monto:{" "}
                            <span className="font-semibold">
                              {formatValueSimple("amount", summaryData.amount)}{" "}
                              {summaryData.currency}
                            </span>
                          </div>
                        )}
                        {summaryData.justification && (
                          <div className="text-xs text-slate-600 dark:text-slate-400 mb-1">
                            Justificación:{" "}
                            <span className="font-semibold">
                              {summaryData.justification}
                            </span>
                          </div>
                        )}
                        {summaryData.category &&
                          summaryData.category !== "No especificado" && (
                            <div className="text-xs text-slate-600 dark:text-slate-400 mb-1">
                              Categoría:{" "}
                              <span className="font-semibold">
                                {summaryData.category}
                              </span>
                            </div>
                          )}
                        {summaryData.subcategory &&
                          summaryData.subcategory !== "No especificado" && (
                            <div className="text-xs text-slate-600 dark:text-slate-400 mb-1">
                              Subcategoría:{" "}
                              <span className="font-semibold">
                                {summaryData.subcategory}
                              </span>
                            </div>
                          )}
                      </div>
                      {/* Artículos */}
                      <div className="flex flex-col items-start md:border-r md:border-orange-100/50 dark:md:border-slate-700/50 pr-0 md:pr-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Package className="h-4 w-4 text-blue-500" />
                          <span className="font-bold text-slate-900 dark:text-slate-100 text-sm">
                            Artículos
                          </span>
                        </div>
                        <div>
                          {isLoadingItems ? (
                            <div className="text-xs text-slate-500">
                              Cargando...
                            </div>
                          ) : displayOrderItems?.length > 0 ? (
                            displayOrderItems.map((item, idx) => (
                              <div
                                key={idx}
                                className="flex items-center gap-2 text-xs mb-1"
                              >
                                <span className="bg-orange-500 text-white rounded px-2 py-0.5">
                                  {idx + 1}
                                </span>
                                <span className="font-semibold">
                                  {item.name ||
                                    item.description ||
                                    `Artículo ${idx + 1}`}
                                </span>
                                {item.quantity_to_order && (
                                  <span className="text-slate-500 ml-2">
                                    x{item.quantity_to_order} {item.unit}
                                  </span>
                                )}
                              </div>
                            ))
                          ) : (
                            <div className="text-xs text-slate-500">
                              Sin artículos
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Información Adicional y Centros de Costo */}
                      <div className="flex flex-col items-start">
                        {/* Información Adicional */}
                        {summaryData.custom_fields &&
                          typeof summaryData.custom_fields === "object" &&
                          Object.keys(summaryData.custom_fields).length > 0 && (
                            <>
                              <div className="flex items-center gap-2 mb-2">
                                <StickyNote className="h-4 w-4 text-blue-500" />
                                <span className="font-bold text-slate-900 dark:text-slate-100 text-sm">
                                  Información Adicional
                                </span>
                              </div>
                              <div className="mb-3">
                                {Object.entries(summaryData.custom_fields).map(
                                  ([key, value]) => (
                                    <div
                                      key={key}
                                      className="text-xs text-slate-600 dark:text-slate-400 mb-1"
                                    >
                                      <span className="font-semibold capitalize">
                                        {key.replace(/_/g, " ")}:
                                      </span>
                                      <span className="ml-1">
                                        {formatValueSimple(key, value) ||
                                          "No especificado"}
                                      </span>
                                    </div>
                                  )
                                )}
                              </div>
                            </>
                          )}

                        {/* Cost Center Allocations */}
                        {data?.cost_center_allocations &&
                          Array.isArray(data.cost_center_allocations) &&
                          data.cost_center_allocations.length > 0 && (
                            <>
                              <div className="flex items-center gap-2 mb-2">
                                <Building className="h-4 w-4 text-green-500" />
                                <span className="font-bold text-slate-900 dark:text-slate-100 text-sm">
                                  Centros de Costo
                                </span>
                              </div>
                              <div className="space-y-1">
                                {data.cost_center_allocations.map(
                                  (allocation, idx) => (
                                    <div
                                      key={allocation.id || idx}
                                      className="flex items-center justify-between text-xs"
                                    >
                                      <div className="flex items-center gap-2">
                                        <span className="bg-green-500 text-white rounded px-2 py-0.5 text-xs font-bold">
                                          {idx + 1}
                                        </span>
                                        <span className="font-semibold text-green-700 dark:text-green-300">
                                          {allocation.cost_center?.name ||
                                            "Centro de Costo"}
                                        </span>
                                      </div>
                                      <div className="text-right ml-2">
                                        <span className="font-bold text-green-700 dark:text-green-300">
                                          {parseFloat(
                                            allocation.allocation_percentage ||
                                              0
                                          ).toFixed(1)}
                                          %
                                        </span>
                                        {allocation.amount && (
                                          <div className="text-green-600 dark:text-green-400">
                                            {allocation.currency || "USD"}{" "}
                                            {parseFloat(
                                              allocation.amount
                                            ).toLocaleString()}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>
                            </>
                          )}
                      </div>
                    </div>
                  </div>
                  <OverviewSection
                    data={data}
                    currentUser={currentUser}
                    clientId={clientId}
                    onItemsUpdated={(updatedItems) => {
                      // Update the local data when items are updated
                      console.log(
                        "Artículos actualizados, actualizando datos locales..."
                      );
                      console.log("Updated items received:", updatedItems);

                      // Update the updatedOrderItems state to reflect changes in summary sections
                      setUpdatedOrderItems(updatedItems);

                      // Calculate new total amount based on updated items
                      const newTotalAmount = updatedItems.reduce(
                        (total, item) => {
                          const itemAmount = parseFloat(
                            item.amount || item.common_price || 0
                          );
                          const quantity = parseFloat(
                            item.quantity_to_order || 0
                          );
                          return total + itemAmount * quantity;
                        },
                        0
                      );

                      console.log(
                        "New total amount calculated:",
                        newTotalAmount
                      );

                      // Update the data with new amount and total_amount (but don't change data.id to avoid triggering useEffect)
                      setData((prevData) => ({
                        ...prevData,
                        amount: newTotalAmount,
                        total_amount: newTotalAmount,
                      }));
                    }}
                    mantisBusiness={mantisBusiness}
                    token={token}
                  />
                </TabsContent>

                <TabsContent
                  value="workflow"
                  className="mt-0 space-y-4 md:space-y-6"
                >
                  <WorkflowSection
                    data={data}
                    clientId={clientId}
                    currentUser={
                      currentUser?.email ||
                      currentUser?.corporateEmail ||
                      currentUser?.personalEmail ||
                      "Usuario"
                    }
                    userApprovalSteps={data?.userApprovalSteps}
                    currentUserId={currentUser?._id}
                    currentUserEmail={
                      currentUser?.email ||
                      currentUser?.corporateEmail ||
                      currentUser?.personalEmail
                    }
                  />
                </TabsContent>

                <TabsContent
                  value="documents"
                  className="mt-0 space-y-4 md:space-y-6"
                >
                  <DocumentsSection
                    data={data}
                    clientId={clientId}
                    iprId={data?.id}
                    currentUser={currentUser}
                  />
                </TabsContent>

                <TabsContent
                  value="legal"
                  className="mt-0 space-y-4 md:space-y-6"
                >
                  <LegalSection data={data} />
                </TabsContent>
              </div>
            </Tabs>
          </div>
        </div>
      </div>

      {/* File Upload Modal */}
      {showFileUpload && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto file-upload-modal">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                  Adjuntar Archivo al IPR
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={removeSelectedFile}
                  className="h-8 w-8 p-0 hover:bg-slate-100 dark:hover:bg-slate-700"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* File Preview */}
              <div className="mb-6">
                <div className="flex items-center space-x-3 p-4 bg-slate-50 dark:bg-slate-700 rounded-xl file-preview">
                  <Paperclip className="h-8 w-8 text-blue-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                      {selectedFile?.name}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {(selectedFile?.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={removeSelectedFile}
                  className="flex-1"
                  disabled={isUploadingFile}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleFileUpload}
                  disabled={isUploadingFile}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white upload-button"
                >
                  {isUploadingFile ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Subiendo...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Upload className="h-4 w-4" />
                      <span>Adjuntar</span>
                    </div>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Sidebar */}
      <div className="w-full md:w-[420px] bg-white/95 dark:bg-slate-800/95 backdrop-blur-xl border-l md:border-l border-orange-200/30 dark:border-slate-700/30 flex flex-col h-screen shadow-2xl shadow-orange-500/5 animate-slide-in-right">
        {/* Mobile Header with Back Button */}
        <div className="md:hidden bg-gradient-to-br from-orange-500 via-amber-500 to-orange-600 text-white p-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="flex items-center space-x-1 text-white hover:bg-white/20 rounded transition-all duration-200 px-2 py-1 text-sm"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="font-medium">Volver</span>
            </Button>
            <div className="flex items-center space-x-2">
              {data.form_submission_id && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleEditIPR}
                  className="flex items-center space-x-1 text-white hover:bg-white/20 rounded transition-all duration-200 px-2 py-1 text-sm"
                >
                  <Edit className="h-4 w-4" />
                  <span className="font-medium">Formulario</span>
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleEditIPRModal}
                className="flex items-center space-x-1 text-white hover:bg-white/20 rounded transition-all duration-200 px-2 py-1 text-sm"
              >
                <Edit className="h-4 w-4" />
                <span className="font-medium">Editar</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => document.getElementById("file-input").click()}
                className="flex items-center space-x-1 text-white hover:bg-white/20 rounded transition-all duration-200 px-2 py-1 text-sm"
              >
                <Upload className="h-4 w-4" />
                <span className="font-medium">Archivo</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Enhanced Sidebar Header */}
        <div
          className={`bg-gradient-to-br text-white p-4 md:p-6 flex-shrink-0 relative overflow-hidden ${
            isIPRApproved
              ? "from-green-500 via-emerald-500 to-green-600"
              : "from-orange-500 via-amber-500 to-orange-600"
          }`}
        >
          {/* Decorative background elements */}
          <div className="absolute top-0 right-0 w-20 h-20 md:w-32 md:h-32 bg-white/10 rounded-full -translate-y-10 md:-translate-y-16 translate-x-10 md:translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-16 h-16 md:w-24 md:h-24 bg-white/5 rounded-full translate-y-8 md:translate-y-12 -translate-x-8 md:-translate-x-12"></div>

          <div className="relative flex items-center space-x-3 md:space-x-4">
            <div className="relative">
              <div className="w-10 h-10 md:w-14 md:h-14 bg-white/20 rounded-xl md:rounded-2xl flex items-center justify-center backdrop-blur-sm border border-white/30">
                {isIPRApproved ? (
                  <CheckCircle2 className="h-5 w-5 md:h-7 md:w-7" />
                ) : (
                  <FileText className="h-5 w-5 md:h-7 md:w-7" />
                )}
              </div>
              <div className="absolute -top-1 -right-1 w-4 h-4 md:w-5 md:h-5 bg-green-400 rounded-full border-1 md:border-2 border-white flex items-center justify-center">
                <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-white rounded-full animate-pulse"></div>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-lg md:text-xl">
                {isIPRApproved
                  ? "Orden de Compra Aprobada"
                  : "Detalles del IPR"}
              </h3>
              <p className="text-sm text-white/90 font-medium">
                {isIPRApproved
                  ? `PO #${purchaseOrder?.id || "N/A"}`
                  : `IPR #${data?.id || "N/A"}`}
              </p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center mt-2 space-y-1 sm:space-y-0 sm:space-x-2">
                <div className="flex items-center space-x-1 bg-white/20 px-2 py-1 rounded-full">
                  <Star className="h-3 w-3 text-yellow-300" />
                  <span className="text-xs font-medium">
                    {getCriticalityText(summaryData.criticality)}
                  </span>
                </div>
                <div className="flex items-center space-x-1 bg-white/20 px-2 py-1 rounded-full">
                  <Calendar className="h-3 w-3" />
                  <span className="text-xs font-medium">
                    {isIPRApproved
                      ? purchaseOrder?.created_at?.slice(0, 10) ||
                        summaryData.createdAt ||
                        "N/A"
                      : summaryData.createdAt}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Sidebar Tabs */}
        <div className="border-b border-orange-200/30 dark:border-slate-700/30 flex-shrink-0 bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm">
          <div className="grid grid-cols-3 p-1 md:p-2 gap-1">
            <button
              onClick={() => setSidebarTab("summary")}
              className={`px-2 md:px-3 py-3 md:py-4 text-xs md:text-sm font-medium transition-all duration-300 relative rounded md:rounded-xl ${
                sidebarTab === "summary"
                  ? "text-orange-600 dark:text-orange-400 bg-white dark:bg-slate-700 shadow-lg shadow-orange-500/10"
                  : "text-slate-600 hover:text-orange-600 dark:text-slate-400 dark:hover:text-orange-400 hover:bg-white/70 dark:hover:bg-slate-700/70"
              }`}
            >
              <div className="flex flex-col items-center space-y-1 md:space-y-2">
                <div className="relative">
                  <Info className="h-4 w-4 md:h-5 md:w-5" />
                  {sidebarTab === "summary" && (
                    <div className="absolute -top-1 -right-1 w-1.5 h-1.5 md:w-2 md:h-2 bg-orange-500 rounded-full"></div>
                  )}
                </div>
                <span className="text-xs leading-tight">Resumen</span>
              </div>
            </button>
            <button
              onClick={() => setSidebarTab("ai-insights")}
              className={`px-2 md:px-3 py-3 md:py-4 text-xs md:text-sm font-medium transition-all duration-300 relative rounded md:rounded-xl ${
                sidebarTab === "ai-insights"
                  ? "text-orange-600 dark:text-orange-400 bg-white dark:bg-slate-700 shadow-lg shadow-orange-500/10"
                  : "text-slate-600 hover:text-orange-600 dark:text-slate-400 dark:hover:text-orange-400 hover:bg-white/70 dark:hover:bg-slate-700/70"
              }`}
            >
              <div className="flex flex-col items-center space-y-1 md:space-y-2">
                <div className="relative">
                  <Settings className="h-4 w-4 md:h-5 md:w-5" />
                  {aiInsights && (
                    <div className="absolute -top-1 -right-1 w-1.5 h-1.5 md:w-2 md:h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  )}
                  {insightsError && (
                    <div className="absolute -top-1 -right-1 w-1.5 h-1.5 md:w-2 md:h-2 bg-red-500 rounded-full"></div>
                  )}
                </div>
                <span className="text-xs leading-tight">Insights IA</span>
              </div>
            </button>
            <button
              onClick={() => setSidebarTab("comments")}
              className={`px-2 md:px-3 py-3 md:py-4 text-xs md:text-sm font-medium transition-all duration-300 relative rounded md:rounded-xl ${
                sidebarTab === "comments"
                  ? "text-orange-600 dark:text-orange-400 bg-white dark:bg-slate-700 shadow-lg shadow-orange-500/10"
                  : "text-slate-600 hover:text-orange-600 dark:text-slate-400 dark:hover:text-orange-400 hover:bg-white/70 dark:hover:bg-slate-700/70"
              }`}
            >
              <div className="flex flex-col items-center space-y-1 md:space-y-2">
                <div className="relative">
                  <MessageSquare className="h-4 w-4 md:h-5 md:w-5" />
                  <div className="absolute -top-1 -right-1 w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full opacity-75"></div>
                </div>
                <span className="text-xs leading-tight">Comentarios</span>
              </div>
            </button>
          </div>
        </div>

        {/* Enhanced Sidebar Content */}
        <div className="flex-1 overflow-y-auto min-h-0 bg-gradient-to-b from-slate-50/50 to-white/50 dark:from-slate-900/50 dark:to-slate-800/50">
          {sidebarTab === "summary" && (
            <div className="p-3 md:p-6">
              <div
                className={`bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl rounded-xl md:rounded-2xl p-3 md:p-5 border glass-morphism animate-fade-in-up ${
                  isIPRApproved
                    ? "border-green-200/30 dark:border-green-700/30 shadow-xl shadow-green-500/5"
                    : "border-orange-200/30 dark:border-slate-700/30 shadow-xl shadow-orange-500/5"
                }`}
              >
                {/* Purchase Order Information (when available) */}
                {isIPRApproved && (
                  <>
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <span className="font-bold text-slate-900 dark:text-slate-100 text-sm">
                        Información de la Orden de Compra
                      </span>
                    </div>
                    <div className="bg-green-50/80 dark:bg-green-900/20 rounded-lg p-3 mb-3 border border-green-200 dark:border-green-800">
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-slate-500 dark:text-slate-400">
                            PO #:
                          </span>
                          <div className="font-semibold text-green-700 dark:text-green-300">
                            {purchaseOrder?.id}
                          </div>
                        </div>
                        <div>
                          <span className="text-slate-500 dark:text-slate-400">
                            Total:
                          </span>
                          <div className="font-semibold text-green-700 dark:text-green-300">
                            {purchaseOrder?.currency || data?.currency || "USD"}{" "}
                            {(
                              purchaseOrder?.total_amount ||
                              data?.amount ||
                              0
                            ).toLocaleString()}
                          </div>
                        </div>
                        <div>
                          <span className="text-slate-500 dark:text-slate-400">
                            Entrega:
                          </span>
                          <div className="font-semibold text-green-700 dark:text-green-300">
                            {purchaseOrder?.expected_delivery_date
                              ? new Date(
                                  purchaseOrder.expected_delivery_date
                                ).toLocaleDateString()
                              : "N/A"}
                          </div>
                        </div>
                        <div>
                          <span className="text-slate-500 dark:text-slate-400">
                            Artículos:
                          </span>
                          <div className="font-semibold text-green-700 dark:text-green-300">
                            {(purchaseOrderItems &&
                            purchaseOrderItems.length > 0
                              ? purchaseOrderItems
                              : orderItems
                            )?.length || 0}
                          </div>
                        </div>
                      </div>
                    </div>
                    <hr className="my-3 border-green-200 dark:border-green-700" />
                  </>
                )}

                {/* Solicitante (first, compact) */}
                <div className="flex items-center gap-2 mb-2">
                  <UserCheck className="h-4 w-4 text-purple-500" />
                  <span className="font-bold text-slate-900 dark:text-slate-100 text-sm">
                    Solicitante
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center">
                    <span className="text-white font-bold text-base">
                      {summaryData.requester.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <div className="font-bold text-slate-900 dark:text-slate-100 text-xs md:text-sm">
                      {summaryData.requester}
                    </div>
                    <div className="text-xs text-slate-600 dark:text-slate-400">
                      Propietario de la Solicitud
                    </div>
                  </div>
                </div>
                <hr className="my-3 border-orange-100 dark:border-slate-700" />

                {/* Detalles de la Solicitud */}
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="h-4 w-4 text-orange-500" />
                  <span className="font-bold text-slate-900 dark:text-slate-100 text-sm">
                    Detalles de la Solicitud
                  </span>
                </div>
                <div className="text-xs text-slate-600 dark:text-slate-400 mb-1">
                  Depto:{" "}
                  <span className="font-semibold">
                    {summaryData.department}
                  </span>
                </div>
                {summaryData.amount > 0 && (
                  <div className="text-xs text-slate-600 dark:text-slate-400 mb-1">
                    Monto:{" "}
                    <span className="font-semibold">
                      {formatValueSimple("amount", summaryData.amount)}{" "}
                      {summaryData.currency}
                    </span>
                  </div>
                )}
                {summaryData.justification && (
                  <div className="text-xs text-slate-600 dark:text-slate-400 mb-1">
                    Justificación:{" "}
                    <span className="font-semibold">
                      {summaryData.justification}
                    </span>
                  </div>
                )}
                {summaryData.category &&
                  summaryData.category !== "No especificado" && (
                    <div className="text-xs text-slate-600 dark:text-slate-400 mb-1">
                      Categoría:{" "}
                      <span className="font-semibold">
                        {summaryData.category}
                      </span>
                    </div>
                  )}
                {summaryData.subcategory &&
                  summaryData.subcategory !== "No especificado" && (
                    <div className="text-xs text-slate-600 dark:text-slate-400 mb-1">
                      Subcategoría:{" "}
                      <span className="font-semibold">
                        {summaryData.subcategory}
                      </span>
                    </div>
                  )}
                <hr className="my-3 border-orange-100 dark:border-slate-700" />

                {/* Artículos */}
                <div className="flex items-center gap-2 mb-2">
                  <Package className="h-4 w-4 text-blue-500" />
                  <span className="font-bold text-slate-900 dark:text-slate-100 text-sm">
                    Artículos
                  </span>
                </div>
                <div className="mb-2">
                  {isLoadingItems ? (
                    <div className="text-xs text-slate-500">Cargando...</div>
                  ) : displayOrderItems?.length > 0 ? (
                    displayOrderItems.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 text-xs mb-1"
                      >
                        <span className="bg-orange-500 text-white rounded px-2 py-0.5">
                          {idx + 1}
                        </span>
                        <span className="font-semibold">
                          {item.name ||
                            item.description ||
                            `Artículo ${idx + 1}`}
                        </span>
                        {item.quantity_to_order && (
                          <span className="text-slate-500 ml-2">
                            x{item.quantity_to_order} {item.unit}
                          </span>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-xs text-slate-500">Sin artículos</div>
                  )}
                </div>
                <hr className="my-3 border-orange-100 dark:border-slate-700" />

                {/* Estado y Prioridad */}
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                  <span className="font-bold text-slate-900 dark:text-slate-100 text-sm">
                    Estado y Prioridad
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs mb-1">
                  <span className="font-bold text-orange-600">Estado:</span>
                  <Badge
                    className={`text-xs font-medium px-2 py-0.5 rounded-full ${getStatusColor(
                      summaryData.status
                    )}`}
                  >
                    {summaryData.status === "approved" ||
                    summaryData.status === "aprobado" ? (
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                    ) : summaryData.status === "rejected" ||
                      summaryData.status === "rechazado" ? (
                      <XCircle className="h-3 w-3 mr-1" />
                    ) : (
                      <Clock className="h-3 w-3 mr-1" />
                    )}
                    <span>{getStatusText(summaryData.status)}</span>
                  </Badge>
                </div>
                <div className="flex items-center gap-2 text-xs mb-1">
                  <span className="font-bold text-orange-600">Prioridad:</span>
                  <Badge
                    className={`text-xs font-medium px-2 py-0.5 rounded-full ${getCriticalityColor(
                      summaryData.criticality
                    )}`}
                  >
                    <Star className="h-3 w-3 mr-1" />
                    <span>{getCriticalityText(summaryData.criticality)}</span>
                  </Badge>
                </div>
                <div className="flex items-center gap-2 text-xs mb-1">
                  <span className="font-bold text-orange-600">Entrega:</span>
                  <Calendar className="h-3 w-3 text-orange-500" />
                  <span>{summaryData.deliveryDate}</span>
                </div>
                <hr className="my-3 border-orange-100 dark:border-slate-700" />

                {/* Información Adicional */}
                {summaryData.custom_fields &&
                  typeof summaryData.custom_fields === "object" &&
                  Object.keys(summaryData.custom_fields).length > 0 && (
                    <>
                      <div className="flex items-center gap-2 mb-2">
                        <StickyNote className="h-4 w-4 text-blue-500" />
                        <span className="font-bold text-slate-900 dark:text-slate-100 text-sm">
                          Información Adicional
                        </span>
                      </div>
                      <div>
                        {Object.entries(summaryData.custom_fields).map(
                          ([key, value]) => (
                            <div
                              key={key}
                              className="text-xs text-slate-600 dark:text-slate-400 mb-1"
                            >
                              <span className="font-semibold capitalize">
                                {key.replace(/_/g, " ")}:
                              </span>
                              <span className="ml-1">
                                {formatValueSimple(key, value) ||
                                  "No especificado"}
                              </span>
                            </div>
                          )
                        )}
                      </div>
                      <hr className="my-3 border-orange-100 dark:border-slate-700" />
                    </>
                  )}

                {/* Cost Center Allocations */}
                {data?.cost_center_allocations &&
                  Array.isArray(data.cost_center_allocations) &&
                  data.cost_center_allocations.length > 0 && (
                    <>
                      <div className="flex items-center gap-2 mb-2">
                        <Building className="h-4 w-4 text-green-500" />
                        <span className="font-bold text-slate-900 dark:text-slate-100 text-sm">
                          Asignación de Centros de Costo
                        </span>
                      </div>
                      <div className="space-y-2">
                        {data.cost_center_allocations.map((allocation, idx) => (
                          <div
                            key={allocation.id || idx}
                            className="bg-green-50/80 dark:bg-green-900/20 rounded-lg p-3 border border-green-200 dark:border-green-800"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-bold">
                                  {idx + 1}
                                </div>
                                <span className="font-semibold text-green-700 dark:text-green-300 text-sm">
                                  {allocation.cost_center?.name ||
                                    "Centro de Costo"}
                                </span>
                              </div>
                              <div className="text-right">
                                <div className="text-sm font-bold text-green-700 dark:text-green-300">
                                  {parseFloat(
                                    allocation.allocation_percentage || 0
                                  ).toFixed(1)}
                                  %
                                </div>
                                {allocation.amount && (
                                  <div className="text-xs text-green-600 dark:text-green-400">
                                    {allocation.currency || "USD"}{" "}
                                    {parseFloat(
                                      allocation.amount
                                    ).toLocaleString()}
                                  </div>
                                )}
                              </div>
                            </div>
                            {allocation.notes && (
                              <div className="text-xs text-slate-600 dark:text-slate-400 mt-1 italic">
                                <span className="font-medium">Notas:</span>{" "}
                                {allocation.notes}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      <hr className="my-3 border-orange-100 dark:border-slate-700" />
                    </>
                  )}
              </div>
            </div>
          )}

          {sidebarTab === "ai-insights" && (
            <div className="p-2 md:p-4">
              <AIInsightsSection
                data={data}
                orderItems={displayOrderItems}
                isLoadingItems={isLoadingItems}
                clientId={clientId}
                insights={aiInsights}
                loading={isLoadingInsights}
                error={insightsError}
                onRegenerateInsights={() => {
                  // Los insights ya vienen en el IPR, no necesitamos regenerar
                  toast({
                    title: "Información",
                    description: "Los insights ya están incluidos en este IPR",
                  });
                }}
                onClearInsights={() => {
                  // No permitir limpiar insights que vienen del IPR
                  toast({
                    title: "Información",
                    description:
                      "Los insights forman parte del IPR y no se pueden eliminar",
                  });
                }}
              />
            </div>
          )}

          {sidebarTab === "comments" && (
            <div className="p-3 md:p-6">
              <CommentsSection
                data={data}
                currentUser={currentUser}
                addLog={() => {}}
                clientId={clientId}
              />
            </div>
          )}
        </div>
      </div>

      {/* Edit IPR Modal */}
      <EditIPRModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        data={data}
        clientId={clientId}
        onUpdate={handleIPRUpdate}
      />
    </div>
  );
}

// Agregar estilos adicionales para completar el diseño
const styleEnhancement = `
  .control-panel-detail {
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
  }
  
  .glass-effect {
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
  }
  
  .shadow-glow {
    box-shadow: 0 10px 40px rgba(249, 115, 22, 0.1);
  }
  
  .border-glow {
    border: 1px solid rgba(249, 115, 22, 0.2);
    box-shadow: 0 0 20px rgba(249, 115, 22, 0.05);
  }
  
  .text-glow {
    text-shadow: 0 0 20px rgba(249, 115, 22, 0.3);
  }
  
  /* File upload specific styles */
  .file-upload-modal {
    animation: slideInUp 0.3s ease-out;
  }
  
  .file-preview {
    transition: all 0.2s ease;
  }
  
  .file-preview:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  .upload-button {
    transition: all 0.2s ease;
  }
  
  .upload-button:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }
  
  @keyframes slideInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

// Inyectar estilos adicionales
if (typeof document !== 'undefined') {
  const existingStyle = document.getElementById('control-panel-styles');
  if (!existingStyle) {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'control-panel-styles';
    styleSheet.textContent = styleEnhancement;
    document.head.appendChild(styleSheet);
  }
}

export default connect(selectAuthState)(ControlPanelDetail); 